import i18n from './index';
import { DEFAULT_LANGUAGE } from './supportedLanguages';

function activeLocale(): string {
  return (i18n.resolvedLanguage || i18n.language || DEFAULT_LANGUAGE) as string;
}

function withFallback<T>(fn: (locale: string) => T): T {
  try {
    return fn(activeLocale());
  } catch (err) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn('[i18n/format] falling back to default locale:', err);
    }
    return fn(DEFAULT_LANGUAGE);
  }
}

export function formatDate(
  value: Date | string | number,
  opts?: Intl.DateTimeFormatOptions
): string {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return withFallback(locale =>
    new Intl.DateTimeFormat(locale, opts ?? { dateStyle: 'medium' }).format(date)
  );
}

export function formatDateTime(
  value: Date | string | number,
  opts?: Intl.DateTimeFormatOptions
): string {
  return formatDate(value, opts ?? { dateStyle: 'medium', timeStyle: 'short' });
}

export function formatNumber(value: number, opts?: Intl.NumberFormatOptions): string {
  if (typeof value !== 'number' || Number.isNaN(value)) return '';
  return withFallback(locale => new Intl.NumberFormat(locale, opts).format(value));
}

export function formatCurrency(
  value: number,
  currency: string = 'INR',
  opts?: Intl.NumberFormatOptions
): string {
  if (typeof value !== 'number' || Number.isNaN(value)) return '';
  return withFallback(locale =>
    new Intl.NumberFormat(locale, { style: 'currency', currency, ...opts }).format(value)
  );
}

export function formatRelativeTime(
  date: Date | string | number,
  baseDate: Date = new Date()
): string {
  const target = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(target.getTime())) return '';
  const diffMs = target.getTime() - baseDate.getTime();
  const diffSec = Math.round(diffMs / 1000);

  const units: Array<[Intl.RelativeTimeFormatUnit, number]> = [
    ['year', 60 * 60 * 24 * 365],
    ['month', 60 * 60 * 24 * 30],
    ['week', 60 * 60 * 24 * 7],
    ['day', 60 * 60 * 24],
    ['hour', 60 * 60],
    ['minute', 60],
    ['second', 1],
  ];

  for (const [unit, perUnit] of units) {
    if (Math.abs(diffSec) >= perUnit || unit === 'second') {
      return withFallback(locale =>
        new Intl.RelativeTimeFormat(locale, { numeric: 'auto' }).format(
          Math.round(diffSec / perUnit),
          unit
        )
      );
    }
  }

  return '';
}
